import {
  Button,
  Divider,
  EDividerVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Grid,
  GridItem,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Coverage from '@/assets/become-a-host/stay/coverage.svg';
import {
  BAR_CHART_2,
  CAMPER,
  CAMPING,
  HEART,
  MONEY,
  USERS,
} from '@/components/switchback/Icon/assets';
import Icon from '@/components/switchback/Icon/IconComponent';
import { ERentalType } from '@/services/analytics/types';

import styles from './BecomeAHost.module.css';
import { useHostingType } from './useHostingType';

export const HostBenefits = () => {
  const intl = useIntl();
  const { rentalType } = useHostingType();
  const content = {
    [ERentalType.RV]: {
      benefits: [
        {
          icon: <Icon width={38} height={38} name={MONEY} />,
          title: intl.formatMessage({ defaultMessage: 'Earn', id: 'W9cTVS' }),
          description: intl.formatMessage({
            defaultMessage: 'each time you host — $688 million paid to hosts since 2015',
            id: 'jsFvej',
          }),
        },

        {
          icon: <Icon width={38} height={38} name={CAMPER} />,
          title: intl.formatMessage({ defaultMessage: 'Partner', id: 'MToUjW' }),
          description: intl.formatMessage({
            defaultMessage: 'with the top innovator in RV rentals',
            id: 'NP5ToH',
          }),
        },
        {
          icon: <Icon width={38} height={38} name={CAMPING} />,
          title: intl.formatMessage({ defaultMessage: 'Share', id: 'OKhRC6' }),
          description: intl.formatMessage({
            defaultMessage: 'your love of travel and the outdoors',
            id: 'KVWx3m',
          }),
        },
        {
          icon: <Icon width={38} height={38} name={HEART} />,
          title: intl.formatMessage({ defaultMessage: 'Connect', id: '+vVZ/G' }),
          description: intl.formatMessage({
            defaultMessage: 'with a 30,000-strong community of hosts',
            id: 'edyQDC',
          }),
        },
      ],
      cta: (
        <Heading variant={EHeadingStyleVariant.H4} className="mx-auto mt-12 text-center max-w-40">
          <FormattedMessage
            defaultMessage="All with the assurance of up to $1 million liability coverage every trip"
            id="8AEeri"
          />
        </Heading>
      ),
    },
    [ERentalType.STAY]: {
      benefits: [
        {
          icon: <Icon width={38} height={38} name={MONEY} />,
          title: intl.formatMessage({ defaultMessage: 'Earn', id: 'W9cTVS' }),
          description: intl.formatMessage({
            defaultMessage:
              'Increase income opportunities for your rental property with guests who love the outdoors.',
            id: '3fyOki',
          }),
        },

        {
          icon: <Icon width={38} height={38} name={HEART} />,
          title: intl.formatMessage({ defaultMessage: 'Partner', id: 'MToUjW' }),
          description: intl.formatMessage({
            defaultMessage:
              'With the leader in outdoor hospitality dedicated to sharing your love of the outdoors.',
            id: 'AImSLX',
          }),
        },
        {
          icon: <Icon width={38} height={38} name={CAMPING} />,
          title: intl.formatMessage({ defaultMessage: 'Stand out', id: 'iIsk9i' }),
          description: intl.formatMessage({
            defaultMessage:
              'Don’t let your stay get lost in a crowd of competitors or be hidden by filters.',
            id: 'CwAmOX',
          }),
        },
        {
          icon: <Icon width={38} height={38} name={BAR_CHART_2} />,
          title: intl.formatMessage({ defaultMessage: 'Optimize', id: '4A8g2w' }),
          description: intl.formatMessage({
            defaultMessage:
              'Fine tune your listing with our expert concierge team to create a high-performing source of income.',
            id: 'ctTsyY',
          }),
        },
      ],
      cta: (
        <div className="flex flex-col items-center mx-auto mt-12 text-center max-w-60">
          <Coverage width={74} height={90} />
          <Heading variant={EHeadingStyleVariant.H4} className="mt-6">
            <FormattedMessage
              defaultMessage="Every stay gets industry-leading insurance coverage with"
              id="39YcU2"
            />
          </Heading>
          <Heading variant={EHeadingStyleVariant.H2Alt} className="mt-2">
            <FormattedMessage defaultMessage="up to $1 Million Liability Coverage" id="2lGiq0" />
          </Heading>
          <Button
            href="https://www.outdoorsy.com/help/protection-package-for-stays"
            label={intl.formatMessage({
              defaultMessage: 'Learn More',
              id: '7DIW6+',
            })}
            className="mt-16"
            target="_blank"
          />
        </div>
      ),
    },

    [ERentalType.CAMPGROUND]: {
      benefits: [
        {
          icon: <Icon width={38} height={38} name={CAMPING} />,
          title: intl.formatMessage({ defaultMessage: 'List', id: 'nOk9mh' }),
          description: intl.formatMessage({
            defaultMessage:
              'List your campground for free with the support of an expert Outdoorsy listing team.',
            id: 'UOk2bJ',
          }),
        },

        {
          icon: <Icon width={38} height={38} name={USERS} />,
          title: intl.formatMessage({ defaultMessage: 'Share', id: 'OKhRC6' }),
          description: intl.formatMessage({
            defaultMessage:
              'Increase your revenue with access to guests who’ve booked over 1m trips and 8m booked nights in the outdoors.',
            id: 'HCHyh0',
          }),
        },
        {
          icon: <Icon width={38} height={38} name={MONEY} />,
          title: intl.formatMessage({ defaultMessage: 'Earn', id: 'W9cTVS' }),
          description: intl.formatMessage({
            defaultMessage:
              'Pay one small commission on confirmed reservations and get paid quickly.',
            id: 'I9o7ad',
          }),
        },
        {
          icon: <Icon width={38} height={38} name={CAMPER} />,
          title: intl.formatMessage({ defaultMessage: 'Partner', id: 'MToUjW' }),
          description: intl.formatMessage({
            defaultMessage:
              'The 24/7 support you need to run and grow your business from a fellow campground operator.',
            id: 'FjedSF',
          }),
        },
      ],
      cta: (
        <div className="flex justify-center mt-16">
          <Button
            href="#faqs"
            label={intl.formatMessage({ defaultMessage: 'See host FAQs', id: 'l0I8Jy' })}
          />
        </div>
      ),
    },
  };

  return (
    <section className={`${styles.spacing} bg-white`}>
      <Grid spacing={6} className="m-0">
        {content[rentalType].benefits.map(({ title, icon, description }) => (
          <GridItem sm={3} xs={12} key={title} className="flex flex-col items-center text-center">
            {icon}
            <Heading variant={EHeadingStyleVariant.H4} className="my-4">
              {title}
            </Heading>
            <Text variant={ETextStyleVariant.MediumRegular} className="max-w-[240px]">
              {description}
            </Text>
          </GridItem>
        ))}
      </Grid>
      {rentalType !== ERentalType.CAMPGROUND && (
        <Divider
          variant={EDividerVariant.Horizontal}
          width="110px"
          className="py-8 mx-auto border-b-4 border-b-canvas-100"
        />
      )}
      {content[rentalType]?.cta}
    </section>
  );
};
